import React, { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Modal } from '../Modal';
import { SubmitButton } from '../SubmitButton';
import { FormGroup } from '../FormGroup';
import { TagRelationshipPicker } from './TagRelationshipPicker';
import { useCreateTag, useUpdateTag, useReprocessTag, useTagsByIds, type Tag, type TagInput } from '../../hooks/useProjectTags';
import { isSuperAdmin } from '../../services/angular-bridge';

interface TagRef { id: string; name: string; color: string; }

interface Props {
  projectId: string;
  tag?: Tag;
  defaultCategoryId?: string;
  defaultColor?: string;
  onClose: () => void;
  onSaved?: (tag: Tag & { inverseObjects: TagRef[]; childrenObjects: TagRef[] }) => void;
}

type RelTab = 'Inverse' | 'Exclusions' | 'Children';

const DEFAULT_COLOR = '#0088cc'
const EMPTY_TAGS: TagRef[] = [];

const TAB_DESCRIPTIONS: Record<RelTab, string> = {
  Inverse: 'Inverse tags are mutually exclusive. Only one of the tags can be applied at a time. Removing one will add the other, and vice versa.',
  Exclusions: 'Tags set as an exclusion are not allowed to co exist with the main tag. If the main tag is applied, any exclusions will be removed. If any excluded tags are applied, the main tag will be removed.',
  Children: 'Tags set as children will pop out and appear as additional options when the main tag is selected from the tag form. Has no automated system effects.',
};

export function TagModal({ projectId, tag, defaultCategoryId, defaultColor = DEFAULT_COLOR, onClose, onSaved }: Props) {
  const createTag = useCreateTag(projectId);
  const updateTag = useUpdateTag(projectId);
  const reprocessTag = useReprocessTag(projectId);

  const isEdit = !!tag?.id;
  const canReprocess = isEdit && isSuperAdmin();
  const isNameEditable = !isEdit || (tag?.isNameEditable ?? true);

  const toId = (i: string | { id: string }) => typeof i === 'string' ? i : i.id;
  const { data: fetchedInverses = EMPTY_TAGS } = useTagsByIds(projectId, (tag?.inverses ?? []).map(toId));
  const { data: fetchedExclusions = EMPTY_TAGS } = useTagsByIds(projectId, (tag?.exclusions ?? []).map(toId));
  const { data: fetchedChildren = EMPTY_TAGS } = useTagsByIds(projectId, (tag?.children ?? []).map(toId));

  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState(tag?.color ?? defaultColor);
  const [inverses, setInverses] = useState<TagRef[]>([]);
  const [exclusions, setExclusions] = useState<TagRef[]>([]);
  const [children, setChildren] = useState<TagRef[]>([]);

  useEffect(() => { setInverses(fetchedInverses as TagRef[]); }, [fetchedInverses]);
  useEffect(() => { setExclusions(fetchedExclusions as TagRef[]); }, [fetchedExclusions]);
  useEffect(() => { setChildren(fetchedChildren as TagRef[]); }, [fetchedChildren]);
  const [activeTab, setActiveTab] = useState<RelTab>('Inverse');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node))
        setPickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerOpen]);

  const isPending = createTag.isPending || updateTag.isPending;
  const isError = createTag.isError || updateTag.isError;

  // Inverse is a 1-to-1 relationship — auto-replace when a second tag is added.
  const handleInversesChange = (tags: TagRef[]) =>
    setInverses(tags.length > 1 ? [tags[tags.length - 1]] : tags);

  const handleSubmit = () => {
    const base: TagInput = {
      name,
      color,
      categoryId: tag?.categoryId ?? defaultCategoryId ?? '',
      projectId,
      inverses: inverses.map(t => t.id),
      exclusions: exclusions.map(t => t.id),
      children: children.map(t => t.id),
    };

    // Save responses may omit relationships � carry the submitted ones through (as Angular did).
    const withRelationships = (saved: Tag) => ({
      ...saved,
      inverses: base.inverses,
      exclusions: base.exclusions,
      children: base.children,
      inverseObjects: inverses,
      childrenObjects: children,
    });

    if (isEdit && tag) {
      updateTag.mutate({
        ...tag, ...base,
        inverseObjects: inverses,
        exclusionObjects: exclusions,
        childrenObjects: children,
      }, {
        onSuccess: (saved) => { onSaved?.(withRelationships(saved)); onClose(); },
      });
    } else {
      createTag.mutate(base, {
        onSuccess: (saved) => { onSaved?.(withRelationships(saved)); onClose(); },
      });
    }
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <div>
        {canReprocess && (
          <button
            type="button"
            className="btn btn-danger"
            title="Re-Process"
            onClick={() => reprocessTag.mutate(tag!.id)}
            disabled={reprocessTag.isPending}
          >
            <i className="fas fa-cog" />
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn btn-default" onClick={onClose} disabled={isPending}>
          Cancel
        </button>
        <SubmitButton
          pending={isPending}
          pendingLabel="Saving…"
          disabled={!name.trim() && isNameEditable}
          onClick={handleSubmit}
          type="button"
        >
          Save
        </SubmitButton>
      </div>
    </div>
  );

  return (
    <Modal
      title={<><i className="fas fa-tags" style={{ color }} />{' '}{isEdit ? 'Edit' : 'Add'} Tag</>}
      onClose={onClose}
      footer={footer}
      bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 16, overflow: 'visible' }}
    >
      <div>
      {name && (
        <h4>
          <i className="fas fa-tags" style={{ color }} /> {name}
        </h4>
      )}
      {tag?.id && <p style={{marginBottom: 0}}>ID: {tag.id}</p>}
      </div>

      <input
        id="tag-name"
        type="text"
        className="form-control"
        placeholder="Tag Name"
        maxLength={255}
        value={name}
        onChange={e => setName(e.target.value)}
        disabled={isPending || !isNameEditable}
      />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          id="tag-color-text"
          type="text"
          className="form-control"
          placeholder="#rrggbb"
          maxLength={7}
          value={color}
          onChange={e => setColor(e.target.value)}
          disabled={isPending}
          style={{ flex: 1 }}
        />
        <div ref={pickerRef} style={{ position: 'relative', display: 'flex' }}>
          <button
            type="button"
            onClick={() => setPickerOpen(o => !o)}
            disabled={isPending}
            style={{ width: 34, height: 34, backgroundColor: color, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
          />
          {pickerOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 10, marginTop: 4 }}>
              <HexColorPicker color={color} onChange={setColor} />
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-default"
          onClick={() => setColor(defaultColor)}
          disabled={isPending}
        >
          Reset
        </button>
      </div>

      {isNameEditable && (
        <div>
          <ul className="nav nav-tabs">
            {(['Inverse', 'Exclusions', 'Children'] as RelTab[]).map(tab => (
              <li key={tab} className={activeTab === tab ? 'active' : ''}>
                <a href="#" onClick={e => { e.preventDefault(); setActiveTab(tab); }}>{tab}</a>
              </li>
            ))}
          </ul>
          <div style={{ border: '1px solid #ddd', padding: '15px', borderTop: 'none' }} >
            <p className="text-muted" style={{ marginBottom: '8px' }}>
              <small>{TAB_DESCRIPTIONS[activeTab]}</small>
            </p>
            {activeTab === 'Inverse' && (
              <TagRelationshipPicker
                id="tag-inverses"
                projectId={projectId}
                selected={inverses}
                onChange={handleInversesChange}
                disabled={isPending}
              />
            )}
            {activeTab === 'Exclusions' && (
              <TagRelationshipPicker
                id="tag-exclusions"
                projectId={projectId}
                selected={exclusions}
                onChange={setExclusions}
                disabled={isPending}
              />
            )}
            {activeTab === 'Children' && (
              <TagRelationshipPicker
                id="tag-children"
                projectId={projectId}
                selected={children}
                onChange={setChildren}
                disabled={isPending}
              />
            )}
          </div>
        </div>
      )}

      {isError && <p className="text-danger">Failed to save tag. Please try again.</p>}
    </Modal>
  );
}
